import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { PublicHeader, PublicFooter } from '../components/PublicLayout';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Radio, Pin, Clock, Users } from 'lucide-react';
import api from '../lib/api';

export default function LiveBlogPage() {
  const { id } = useParams();
  const [blog, setBlog] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadBlog(); const interval = setInterval(loadUpdates, 15000); return () => clearInterval(interval); }, [id]);

  const loadBlog = async () => {
    try {
      const res = await api.get(`/api/live/${id}`);
      setBlog(res.data);
      setUpdates(res.data.updates || []);
    } catch {} finally { setLoading(false); }
  };

  const loadUpdates = async () => {
    try {
      const res = await api.get(`/api/live/${id}`);
      setUpdates(res.data.updates || []);
    } catch {}
  };

  if (loading) return (<><PublicHeader /><main className="bg-[#FBF7EF] min-h-screen"><div className="max-w-[800px] mx-auto px-4 py-12"><Skeleton className="h-12 w-2/3 mb-4" /><Skeleton className="h-6 w-1/3 mb-8" />{[1,2,3].map(i => <Skeleton key={i} className="h-24 mb-4 rounded-xl" />)}</div></main><PublicFooter /></>);

  if (!blog) return (<><PublicHeader /><main className="bg-[#FBF7EF] min-h-screen flex items-center justify-center"><p className="text-[#5B616E]">Live blog not found</p></main><PublicFooter /></>);

  const pinnedUpdates = updates.filter(u => u.is_pinned);
  const regularUpdates = updates.filter(u => !u.is_pinned);

  return (
    <>
      <PublicHeader />
      <main className="bg-[#FBF7EF] min-h-screen">
        <section className="max-w-[800px] mx-auto px-4 sm:px-6 py-12">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-3">
              {blog.is_active && (
                <Badge className="bg-[#B3261E] text-white animate-pulse text-[10px]">
                  <Radio className="w-3 h-3 mr-1" /> LIVE
                </Badge>
              )}
              <Badge variant="outline" className="text-[10px]">
                <Clock className="w-3 h-3 mr-1" /> {updates.length} updates
              </Badge>
            </div>
            <h1 className="news-masthead text-3xl font-bold">{blog.title}</h1>
            {blog.description && <p className="text-[#5B616E] mt-2">{blog.description}</p>}
          </div>

          {/* Pinned Updates */}
          {pinnedUpdates.length > 0 && (
            <div className="mb-8">
              <h2 className="text-sm font-bold flex items-center gap-1 mb-3"><Pin className="w-4 h-4 text-[#B89B5E]" /> Key Moments</h2>
              {pinnedUpdates.map((update, i) => (
                <Card key={i} className="bg-[#B89B5E]/5 border-[#B89B5E]/20 mb-3">
                  <CardContent className="py-4">
                    <p className="text-sm font-medium">{update.content}</p>
                    <div className="flex items-center gap-2 mt-2 text-xs text-[#5B616E]">
                      <span>{update.author_name || 'Staff'}</span>
                      <span>•</span>
                      <span>{update.created_at ? new Date(update.created_at).toLocaleTimeString() : ''}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Timeline */}
          <div ref={scrollRef} className="space-y-4">
            {regularUpdates.map((update, i) => (
              <div key={i} className="flex gap-4">
                <div className="flex flex-col items-center">
                  <div className="w-3 h-3 rounded-full bg-[#1E5A8A] flex-shrink-0 mt-1.5" />
                  {i < regularUpdates.length - 1 && <div className="w-px flex-1 bg-[#E7E0D6] mt-1" />}
                </div>
                <Card className="bg-white border-[#E7E0D6] flex-1 mb-1">
                  <CardContent className="py-4">
                    <div className="flex items-center gap-2 mb-2 text-xs text-[#5B616E]">
                      <span className="font-medium text-[#121316]">{update.author_name || 'Newsroom'}</span>
                      <span>•</span>
                      <span>{update.created_at ? new Date(update.created_at).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'}) : ''}</span>
                    </div>
                    <p className="text-sm leading-relaxed">{update.content}</p>
                    {update.metadata?.image_url && <img src={update.metadata.image_url} alt="" className="mt-3 rounded-lg w-full max-h-64 object-cover" />}
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>

          {updates.length === 0 && (
            <Card className="bg-white border-[#E7E0D6] p-12 text-center">
              <Radio className="w-12 h-12 mx-auto mb-4 text-[#E7E0D6]" />
              <p className="text-[#5B616E]">No updates yet. Check back soon.</p>
            </Card>
          )}
        </section>
      </main>
      <PublicFooter />
    </>
  );
}
